import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, Button } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { ProjectGanttTask, ProjectPhase } from '../types';
import {
  MODE_CHIP,
  MODE_LABEL,
  fmtDate,
  isTaskDone,
  isTaskOverdue,
  statusChip,
} from './workspaceHelpers';

export interface TaskDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  task: ProjectGanttTask | null;
  /** Todas as tarefas do projeto (resolve títulos/estado das dependências). */
  allTasks: ProjectGanttTask[];
  phases: ProjectPhase[];
  /** Abre o `TaskModal` em modo edição (gerido pela página). */
  onEdit: () => void;
}

/**
 * Drawer de detalhe da tarefa (protótipo G2). Mostra o ESTADO da tarefa —
 * conclusão acontece pelo ticket (kanban/detalhe), não aqui. O Gantt reflete
 * via refetch após qualquer mudança.
 */
export const TaskDrawer: React.FC<TaskDrawerProps> = ({ isOpen, onClose, task, allTasks, phases, onEdit }) => {
  const navigate = useNavigate();

  const taskById = useMemo(() => {
    const map = new Map<string, ProjectGanttTask>();
    allTasks.forEach((t) => map.set(t.id, t));
    return map;
  }, [allTasks]);

  const phase = useMemo(
    () => (task?.phaseId ? phases.find((p) => p.id === task.phaseId) : undefined),
    [task?.phaseId, phases],
  );

  const preds = useMemo(() => {
    if (!task) return [];
    return (task.dependencies ?? []).map((d) => ({ dep: d, pred: taskById.get(d.taskId) }));
  }, [task, taskById]);

  const pendingPreds = preds.filter((x) => x.pred && !isTaskDone(x.pred));

  // Progresso da fase (tarefas concluídas / total na mesma fase).
  const phaseProgress = useMemo(() => {
    if (!task?.phaseId) return null;
    const siblings = allTasks.filter((t) => t.phaseId === task.phaseId);
    const done = siblings.filter(isTaskDone).length;
    return { done, total: siblings.length, pct: siblings.length ? Math.round((done / siblings.length) * 100) : 0 };
  }, [task?.phaseId, allTasks]);

  if (!task) return null;

  const chip = statusChip(task.ticket?.statusCategory);
  const overdue = isTaskOverdue(task);
  const done = isTaskDone(task);

  const footer = (
    <>
      {task.ticket?.ticketId && (
        <Button
          variant="secondary"
          icon="external-link"
          iconPosition="right"
          onClick={() => navigate(`/helpdesk/tickets/${task.ticket?.ticketId}`)}
          data-testid="project-ws-drawer-open-ticket"
        >
          Abrir ticket
        </Button>
      )}
      <Button icon="edit" onClick={onEdit} data-testid="project-ws-drawer-edit">
        Editar
      </Button>
    </>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      icon={<Icon name="check-square" className="w-5 h-5" />}
      title={task.ticket?.title || 'Tarefa'}
      subtitle={task.ticket?.ticketNumber ? `#${task.ticket.ticketNumber}` : undefined}
      footer={footer}
    >
      <div className="p-6 space-y-5" data-testid="project-ws-task-drawer">
        {/* Chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full font-medium"
            style={{ fontSize: 12, backgroundColor: chip.bg, color: chip.fg }}
          >
            {chip.label}
          </span>
          {phase && (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full font-medium"
              style={{ fontSize: 12, backgroundColor: `${phase.color}1f`, color: phase.color }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: phase.color }} />
              {phase.name}
            </span>
          )}
          {task.isMilestone && (
            <span
              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-medium"
              style={{ fontSize: 12, backgroundColor: 'rgba(139, 92, 246, 0.14)', color: '#6d28d9' }}
            >
              ◆ Marco
            </span>
          )}
        </div>

        {/* Responsável + datas */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs mb-0.5" style={{ color: 'var(--theme-muted-text)' }}>
              Responsável
            </div>
            <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--theme-card-text)' }}>
              <Icon name="user" className="w-3.5 h-3.5" style={{ color: 'var(--theme-muted-text)' }} />
              {task.ticket?.assigneeName || 'Sem responsável'}
            </div>
          </div>
          <div>
            <div className="text-xs mb-0.5" style={{ color: 'var(--theme-muted-text)' }}>
              {task.isMilestone ? 'Data' : 'Prazo'}
            </div>
            <div
              className="flex items-center gap-1.5 text-sm font-medium"
              style={{ color: overdue ? '#dc2626' : 'var(--theme-card-text)' }}
            >
              <Icon name="calendar" className="w-3.5 h-3.5" />
              {task.isMilestone ? fmtDate(task.startDate) : `${fmtDate(task.startDate)} – ${fmtDate(task.endDate)}`}
              {overdue && <span className="text-xs font-normal">(atrasada)</span>}
            </div>
          </div>
        </div>

        {/* Estado */}
        {task.blocked ? (
          <StateBox
            tone="danger"
            icon="lock"
            title="Bloqueada"
            text="Conclua as predecessoras em modo Bloquear antes de avançar este ticket."
          />
        ) : pendingPreds.length > 0 ? (
          <StateBox
            tone="warn"
            icon="clock"
            title="Predecessora pendente"
            text="Há predecessoras não concluídas. O avanço do ticket será avisado conforme o modo."
          />
        ) : (
          <StateBox
            tone="ok"
            icon="check-circle"
            title={done ? 'Concluída' : 'Liberada'}
            text={done ? 'Esta tarefa já foi concluída pelo ticket.' : 'Sem predecessoras pendentes — pode avançar.'}
          />
        )}

        {/* Dependências */}
        <div>
          <div className="text-xs font-medium mb-2" style={{ color: 'var(--theme-muted-text)' }}>
            Dependências
          </div>
          {preds.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
              Nenhuma dependência.
            </p>
          ) : (
            <div className="space-y-2">
              {preds.map(({ dep, pred }, i) => {
                const predDone = pred ? isTaskDone(pred) : false;
                const modeChip = MODE_CHIP[dep.mode];
                return (
                  <div
                    key={`${dep.taskId}-${i}`}
                    className="flex items-center gap-2 rounded-lg border px-3 py-2"
                    style={{ borderColor: 'var(--theme-card-border)' }}
                  >
                    <span style={{ color: predDone ? '#16a34a' : 'var(--theme-muted-text)' }}>
                      {predDone ? '✓' : '○'}
                    </span>
                    <span className="text-sm flex-1 truncate" style={{ color: 'var(--theme-card-text)' }}>
                      {pred?.ticket?.title || 'Tarefa removida'}
                    </span>
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded font-medium"
                      style={{ fontSize: 11, backgroundColor: modeChip.bg, color: modeChip.fg }}
                    >
                      {MODE_LABEL[dep.mode]}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Progresso da fase */}
        {phase && phaseProgress && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium" style={{ color: 'var(--theme-muted-text)' }}>
                Progresso da fase · {phase.name}
              </span>
              <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                {phaseProgress.done}/{phaseProgress.total} · {phaseProgress.pct}%
              </span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--theme-border)' }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${phaseProgress.pct}%`,
                  backgroundColor: phaseProgress.pct === 100 ? '#22c55e' : phase.color,
                }}
              />
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

const StateBox: React.FC<{
  tone: 'danger' | 'warn' | 'ok';
  icon: 'lock' | 'clock' | 'check-circle';
  title: string;
  text: string;
}> = ({ tone, icon, title, text }) => {
  const tones = {
    danger: { bg: '#fef2f2', border: '#fecaca', fg: '#b91c1c', body: '#7f1d1d' },
    warn: { bg: '#fffbeb', border: '#fde68a', fg: '#b45309', body: '#92400e' },
    ok: { bg: '#f0fdf4', border: '#bbf7d0', fg: '#15803d', body: '#166534' },
  }[tone];
  return (
    <div className="rounded-lg border p-3 flex gap-2" style={{ backgroundColor: tones.bg, borderColor: tones.border }}>
      <Icon name={icon} className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: tones.fg }} />
      <div>
        <div className="text-sm font-semibold" style={{ color: tones.fg }}>
          {title}
        </div>
        <p className="text-xs" style={{ color: tones.body }}>
          {text}
        </p>
      </div>
    </div>
  );
};

TaskDrawer.displayName = 'TaskDrawer';
